/**
 * Utility methods for components to determine UI states across the app.
 *
 * @module lib/UIStateUtils
 */
import { createSelector } from 'reselect';

import * as Constants from '../constants';
import { AppActions, AppConstants } from '../constants/App';
import { NowPlayingKeys } from '../constants/NowPlaying';
import { ScrollKeys } from '../constants/Scroll';
import { getInDomain } from '../domains/externalUtils';
import * as ListenerUtils from '../lib/ListenerUtils';
import { getTrackTemplatePath, isCustomTrack } from '../lib/PlaylistTrackUtils';
import { type ValueOf } from '../types';

import { getDominantColor, isColorDark } from './ArtUtils';

export function isTunerBarOpen(state: $TSFixMe): boolean {
  const hasStation = getInDomain('playback.utils.listenerHasStations', false)(state);
  const hasCollectedItems = getInDomain('collection.utils.collectionHasItems', false)(state);
  return hasStation || hasCollectedItems;
}

/**
 * Checks state to see if now playing UI is expanded to show session history
 *
 * @param {object} state - redux state
 * @returns {boolean} - is session history open?
 */
export function getIsSessionHistoryOpen(state: $TSFixMe): boolean {
  return state.nowPlaying.get(NowPlayingKeys.IS_SESSION_HISTORY_OPEN_KEY);
}

/**
 * Checks state to see if window is scrolled past now playing top info
 *
 * @param {object} state - redux state
 * @returns {boolean} - is now playing top UI component in view?
 */
export function getIsNowPlayingTopInView(state: $TSFixMe): boolean {
  return state.scroll.get(ScrollKeys.IS_NOWPLAYING_TOP_IN_VIEW);
}

/**
 * Checks state to see if nowplaying route is active. Also returns true if session history is open since it is a child route
 *
 * @param {object} state - redux state
 * @returns {boolean} - is now playing open?
 */
export function getIsNowPlayingOpen(state: $TSFixMe): boolean {
  return state.nowPlaying.get(NowPlayingKeys.IS_NOW_PLAYING_OPEN_KEY);
}

/**
 * Selector to check whether or not scrolling is enabled in the UI.
 *
 * @type Function
 * @param {object} state The current state
 * @returns {boolean} Returns true if scrolling is enabled, false otherwise
 */
export const getIsScrollingEnabled = createSelector(
  state => (state as $TSFixMe).ui,
  ui => ui.get(AppActions.SCROLLING_ENABLED),
);

/**
 * Selector to check whether or not scrolling is enabled in the body of the app.
 *
 * @type Function
 * @param {object} state The current state
 * @returns {boolean} Returns true if scrolling is enabled in the body, false otherwise
 */
export const getIsBodyScrollingEnabled = createSelector(
  state => (state as $TSFixMe).ui,
  ui => ui.get(AppActions.BODY_SCROLLING_ENABLED),
);

/**
 * Goes through the circumstances that cause a track's template to be rendered on Now Playing.
 *
 * @param {object} state - redux state
 * @returns {boolean} - should <TrackTemplate /> be rendered?
 */
export function getIsTrackTemplateRendered(state: $TSFixMe): boolean {
  const track = getInDomain('playback.utils.getCurrentTrackFromActivePlaylist', null)(state);
  if (track && !getIsSessionHistoryOpen(state) && getIsNowPlayingOpen(state)) {
    const templatePath = getTrackTemplatePath(track);

    // template is only rendered if path is defined and is not default template path (default was phased out with redesign)
    return !!templatePath && !templatePath.includes('/default.vm');
  }
  return false;
}

/**
 * If now playing station list is hovered and the right rail UI state is active, some UI elements on now playing
 * animate to the right to make room for the expanded station list
 *
 * @param {object} state - redux state
 * @returns {boolean} Returns true if ui element should be offset, false otherwise
 */
export function getIsStationListHovered(state: $TSFixMe): boolean {
  return state.ui.get(Constants.Station.StationConstants.IS_STATION_LIST_HOVERED);
}

/**
 * Checks state to see if search overlay is active
 *
 * @param {object} state - redux state
 * @returns {boolean} - is search UI component in view?
 */
export function getIsSearchOpen(state: $TSFixMe): boolean {
  // Added a guard for unit tests
  return (
    state.application &&
    state.application.get(
      getInDomain('application.constants.ApplicationConstants.ApplicationStateKeys.IS_SEARCH_OPEN'),
    )
  );
}

export function getIsMainSearchActive(state: $TSFixMe): boolean {
  return state.ui.getIn([
    getInDomain('searchT3.constants.SearchKeys.MAIN_SEARCH', 'mainSearch'),
    getInDomain('searchT3.constants.SearchKeys.SEARCH_ACTIVE', false),
  ]);
}

export function getAppBg(state: $TSFixMe): $TSFixMe {
  return state.ui.get(Constants.App.AppBgKeys.APP_BG);
}

export function getCurrentDominantColor(state: $TSFixMe): string {
  return ListenerUtils.hasPremiumFeatures(state)
    ? state.ui.get(Constants.App.AppBgKeys.DOMINANT_COLOR)
    : '';
}

export function isCurrentDominantColorDark(state: $TSFixMe): boolean {
  return isColorDark(getCurrentDominantColor(state));
}

export function getNavContentTheme(state: $TSFixMe): ValueOf<typeof NavThemes> {
  const { NavThemes, AppBgKeys } = Constants.App;
  let navTheme = state.ui.getIn([AppBgKeys.APP_BG, AppBgKeys.NAV_THEME]);
  if (navTheme === NavThemes.DARK_TO_LIGHT) {
    const isScrolled = state.scroll.get(ScrollKeys.IS_SCROLLED);
    navTheme = isScrolled ? NavThemes.LIGHT : NavThemes.DARK;
  }
  if (navTheme === NavThemes.TRANSPARENT) {
    navTheme = isCurrentDominantColorDark(state) ? NavThemes.DARK : NavThemes.LIGHT;
  }
  return navTheme;
}

export function getTrackDominantColor(state: $TSFixMe, track: $TSFixMe): string {
  if (!ListenerUtils.hasPremiumFeatures(state)) {
    return '';
  }

  // this is an on-demand track
  let color = track && getDominantColor(track);
  if (!color) {
    // this is a station track, so we need to get the annotations
    if (isCustomTrack(track)) {
      // Custom content stations always show blue
      return AppConstants.PANDORA_BLUE;
    }
    const currentTrackPandoraId = track && track.get && track.get('pandoraId');
    const trackAnnotations = getInDomain('catalog.utils.getAnnotations', null)(
      state,
      currentTrackPandoraId,
    );
    if (trackAnnotations && trackAnnotations.size) {
      // fall back to the default dominantColor if we know we're not getting one
      color = getDominantColor(trackAnnotations) || AppConstants.DEFAULT_GREY.slice(1);
    }
  }
  return color && `#${color}`;
}

export function getScreenWidth(state: $TSFixMe): $TSFixMe {
  return state.ui.get(Constants.Screen.ScreenKeys.CURRENT_SCREEN_WIDTH);
}

export function getScreenHeight(state: $TSFixMe): $TSFixMe {
  return state.ui.get(Constants.Screen.ScreenKeys.CURRENT_SCREEN_HEIGHT);
}

export function getIsScrolled(state: $TSFixMe): $TSFixMe {
  return state.scroll.get(ScrollKeys.IS_SCROLLED);
}
